import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Column, Row, Text, Icon, Card, Loader } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useAppDispatch } from "src/store/store";
import { analyzeEvidenceAsync } from "src/store/inspectionResultsSlice";
import InspectionService, { BlurryImageEntry, ImageType } from "src/services/inspectionService";
import ManualCaptureMultipleStep from "../steps/ManualCaptureMultipleStep";
import "./blurReviewPage.scss";

// Reuses the review page's poll cadence: while AI analysis is still running the
// blurry set is not final, so we re-check on this interval.
const POLL_INTERVAL_MS = 3000;

/**
 * A stable client-side key for one blurry image. The backend keys entries by
 * (containerId, imageType); evidenceId disambiguates multiple blurry images on the same
 * container+type, but evidenceId is optional/nullable, so the entry's position in the
 * blurryImages array is folded in too -- this guarantees a unique key even if the
 * backend ever returns two entries sharing container+type with no evidenceId. Used both
 * as the React list key and as the index into the per-card retake state maps.
 */
const entryKey = (entry: BlurryImageEntry, index: number): string =>
    `${index}|${entry.containerId}|${entry.imageType}|${entry.evidenceId ?? ""}`;

const IMAGE_TYPE_LABEL: Record<ImageType, string> = {
    CONTAINER: "Container",
    LABEL: "Label",
};

/**
 * Optional screen shown after the final inspection question and before the review page.
 * Lists images the AI judged too blurry to assess (keyed by container + image type) and
 * lets the inspector retake any of them. A retake uploads a fresh image and queues async
 * VLM re-analysis (same /analyze-evidence-async path the capture flow uses); the inspector
 * does not wait for re-analysis to finish. Retaking is optional: "Continue to Review" is
 * always enabled, and the review page's own polling + fix/override flow handles any
 * result still pending or blurry. When nothing is blurry, the page redirects straight to
 * review.
 */
const BlurReviewPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // null while the first blurry-images response is still outstanding.
    const [blurryImages, setBlurryImages] = useState<BlurryImageEntry[] | null>(null);
    // True while AI analysis is still running server-side (status PROCESSING): the set is
    // not final yet, so we keep polling and show "checking blur images...".
    const [isChecking, setIsChecking] = useState(true);
    // Per-card local state: which blurry images the user has already retaken this session.
    const [retakenKeys, setRetakenKeys] = useState<Record<string, boolean>>({});
    // Local object-URL preview of the freshly captured image per card, so the new photo
    // shows immediately without waiting on the backend / re-fetching blurry-images.
    const [retakenPreviews, setRetakenPreviews] = useState<Record<string, string>>({});
    // Keys of cards currently uploading a freshly captured image (a Set, not a single
    // key, so two concurrent retakes each keep their own spinner instead of one card's
    // finally clearing another's).
    const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
    // Per-card upload error message, shown on the card instead of only console.error.
    // Cleared at the start of the next retake attempt for that card.
    const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
    // The entry currently being retaken (with its array index, needed for entryKey): when
    // set, the camera capture screen is shown.
    const [capturingEntry, setCapturingEntry] = useState<{ entry: BlurryImageEntry; index: number } | null>(null);

    const goToReview = useCallback(() => {
        navigate(`/configurationDrivenInspections/${inspectionId}/review`, { replace: true });
    }, [inspectionId, navigate]);

    // Keep a live ref of the preview URLs so the unmount cleanup revokes the latest set.
    const retakenPreviewsRef = useRef(retakenPreviews);
    retakenPreviewsRef.current = retakenPreviews;
    useEffect(() => {
        return () => {
            Object.values(retakenPreviewsRef.current).forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    // Fetch blurry images on mount, then poll while the backend reports PROCESSING. Once
    // READY: redirect to review if there is nothing blurry, otherwise render the list.
    useEffect(() => {
        if (!inspectionId) return;
        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | undefined;

        const check = async () => {
            try {
                const res = await InspectionService.getBlurryImages(inspectionId);
                if (cancelled) return;

                if (res.status === "PROCESSING") {
                    setIsChecking(true);
                    return; // keep polling
                }

                // READY: stop polling and settle the UI.
                setIsChecking(false);
                if (intervalId) clearInterval(intervalId);
                if (res.blurryImages.length === 0) {
                    goToReview();
                    return;
                }
                setBlurryImages(res.blurryImages);
            } catch (err) {
                // On error, don't trap the inspector on this optional screen: stop
                // checking and fall through to review.
                console.error("Failed to fetch blurry images:", err);
                if (cancelled) return;
                setIsChecking(false);
                if (intervalId) clearInterval(intervalId);
                goToReview();
            }
        };

        check();
        intervalId = setInterval(check, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [inspectionId, goToReview]);

    // Upload a freshly captured image for one blurry entry and queue async re-analysis of
    // its affected questions. Marks the card "retaken" (the "NEW" / in-progress state) on
    // success. The image comes from the in-app camera (ManualCaptureMultipleStep).
    const retake = async (entry: BlurryImageEntry, index: number, file: File) => {
        if (!inspectionId) return;
        const key = entryKey(entry, index);
        setUploadingKeys((prev) => new Set(prev).add(key));
        // Clear any previous error for this card now that a new attempt is starting.
        setUploadErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
        try {
            const payload = new FormData();
            payload.append("inspection_id", inspectionId);
            payload.append("file", file);
            entry.affectedQuestionIds.forEach((qId) => payload.append("question_ids", qId));
            payload.append("object_id", entry.containerId);
            // Retake replaces the stale blurry result in place (reuse resultId) instead
            // of appending a duplicate result for the same (question_id, object_id).
            payload.append("replace_existing", "true");

            await dispatch(analyzeEvidenceAsync(payload)).unwrap();
            setRetakenKeys((prev) => ({ ...prev, [key]: true }));
            // Show the just-captured image locally. Revoke any previous preview for this
            // card first to avoid leaking object URLs.
            setRetakenPreviews((prev) => {
                if (prev[key]) URL.revokeObjectURL(prev[key]);
                return { ...prev, [key]: URL.createObjectURL(file) };
            });
        } catch (err) {
            console.error("Retake upload failed:", err);
            setUploadErrors((prev) => ({ ...prev, [key]: "Upload failed. Please try again." }));
        } finally {
            setUploadingKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    // Still checking (or redirecting away because nothing was blurry). A "Continue to
    // Review" escape hatch is shown below the spinner so a result stuck PENDING on the
    // backend can't trap the inspector here indefinitely — this screen is optional.
    if (isChecking || blurryImages === null) {
        return (
            <div className="blur-review-page">
                <Column
                    height="100vh"
                    width="100%"
                    alignmentVertical="center"
                    alignmentHorizontal="center"
                    spacing="300"
                    spacingInset="none 500"
                >
                    <Loader size="medium" type="circular" />
                    <Text type="h300" color="secondary">
                        Checking blur images...
                    </Text>
                    <button className="blur-review-page__continue-link" onClick={goToReview}>
                        Continue to Review
                    </button>
                </Column>
            </div>
        );
    }

    // Retaking: show the in-app camera (same component the inspection capture steps use).
    // Captured files come back via onSubmitFiles; we upload the first and re-analyze.
    if (capturingEntry) {
        const { entry, index } = capturingEntry;
        const typeLabel = IMAGE_TYPE_LABEL[entry.imageType];
        return (
            <ManualCaptureMultipleStep
                humanText={`Retake the ${typeLabel.toLowerCase()} photo for ${entry.containerId}`}
                activeQuestionIds={entry.affectedQuestionIds}
                maxImages={1}
                onCancel={() => setCapturingEntry(null)}
                onSubmitFiles={(files) => {
                    setCapturingEntry(null);
                    if (files.length > 0) {
                        void retake(entry, index, files[0]);
                    }
                }}
            />
        );
    }

    return (
        <div className="blur-review-page pwa-scrollable">
            <Column spacingInset="500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div className="blur-review-back-button" role="button" onClick={() => navigate("/")}>
                        <Icon tokens={arrowLeftTokens} color="primary" />
                    </div>
                    <Text type="b300" color="primary">
                        Retake Blurry Image
                    </Text>
                </Row>

                <Column spacing="200">
                    <Text type="h400" color="primary">
                        Some photos are too blurry to assess
                    </Text>
                    <Text type="b300" color="secondary">
                        Retake blurry photos. AI will reassess checkpoints and replace the
                        originals automatically.
                    </Text>
                </Column>

                <Column spacing="300">
                    {blurryImages.map((entry, index) => {
                        const key = entryKey(entry, index);
                        const retaken = Boolean(retakenKeys[key]);
                        const uploading = uploadingKeys.has(key);
                        return (
                            <BlurryImageCard
                                key={key}
                                entry={entry}
                                retaken={retaken}
                                uploading={uploading}
                                uploadError={uploadErrors[key]}
                                previewUrl={retakenPreviews[key]}
                                onRetake={() => setCapturingEntry({ entry, index })}
                            />
                        );
                    })}
                </Column>

                {/* Disabled only while a retake upload is in flight: the upload is what
                    flips the result back to PENDING, so leaving mid-upload means
                    InspectionReviewPage loads the old, already-complete result and never
                    shows the new one until a refresh. Not gated on retaking being done at
                    all -- retaking is the inspector's choice, and /review already
                    tolerates pending/blurry results via its own polling and fix/override
                    flow. */}
                <button
                    className="blur-review-page__continue-btn"
                    onClick={goToReview}
                    disabled={uploadingKeys.size > 0}
                >
                    CONTINUE TO REVIEW
                </button>
            </Column>
        </div>
    );
};

type BlurryImageCardProps = {
    entry: BlurryImageEntry;
    retaken: boolean;
    uploading: boolean;
    uploadError?: string;
    previewUrl?: string;
    onRetake: () => void;
};

/** One blurry-image card: thumbnail with a BLURRY/NEW badge, metadata, and a retake button. */
const BlurryImageCard: React.FC<BlurryImageCardProps> = ({
    entry,
    retaken,
    uploading,
    uploadError,
    previewUrl,
    onRetake,
}) => {
    // Show the freshly captured local image once retaken, else the original (blurry) one.
    const thumbnailUrl = previewUrl || entry.presignedUrl;
    // The AI reason can be long. Clamp it by default and let the inspector expand it;
    // no detail is lost, the card just stays short in the common case.
    const [reasonExpanded, setReasonExpanded] = useState(false);
    const reasonText = entry.blurryReason || "Image is too blurry to assess";
    // Only worth a toggle when the reason is long enough to actually get clamped.
    const isReasonLong = reasonText.length > 100;
    return (
        <Card spacingInset="none">
            <Row className="blur-review-card-row" spacing="300" spacingInset="400" alignmentVertical="stretch" widths={["fit", "fill"]}>
                <div className="blur-review-thumb">
                    {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt={`${entry.containerId} ${entry.imageType}`} />
                    ) : (
                        <div className="blur-review-thumb__placeholder" />
                    )}
                    <span className={`blur-review-badge ${retaken ? "blur-review-badge--new" : "blur-review-badge--blurry"}`}>
                        {retaken ? "NEW" : "BLURRY"}
                    </span>
                </div>

                <div className="blur-review-card-content">
                    <Text type="h300" color="primary">
                        {entry.containerId}
                    </Text>
                    <div className="blur-review-type-badge">
                        {IMAGE_TYPE_LABEL[entry.imageType]}
                    </div>
                    {retaken ? (
                        <Text type="b200" color="secondary">
                            AI Analysis in progress..
                        </Text>
                    ) : (
                        <div className="blur-review-reason">
                            <Text
                                type="b200"
                                color="secondary"
                                className={reasonExpanded ? undefined : "blur-review-reason__text--clamped"}
                            >
                                {reasonText}
                            </Text>
                            {isReasonLong && (
                                <button
                                    type="button"
                                    className="blur-review-reason__toggle"
                                    onClick={() => setReasonExpanded((v) => !v)}
                                >
                                    {reasonExpanded ? "Show less" : "Show more"}
                                </button>
                            )}
                        </div>
                    )}

                    {uploadError && !uploading && (
                        <Text type="b100" color="error">
                            {uploadError}
                        </Text>
                    )}

                    {uploading ? (
                        <Loader size="small" type="circular" />
                    ) : !retaken ? (
                        <button className="blur-review-page__retake-btn" onClick={onRetake}>
                            RETAKE PHOTO
                        </button>
                    ) : null}
                </div>
            </Row>
        </Card>
    );
};

export default BlurReviewPage;
